import React, { useState, useCallback } from 'react';
import { Pencil, Trash2, ArrowUpDown, Download } from 'lucide-react';
import { useBulleAuSurvol } from '../BulleSurvol';
import { delaiEnMots } from './leviersServis';

/**
 * Le journal : un relevé par ligne, trié et paginé par le serveur.
 *
 * Bâti sur le modèle de `NotesArchive.jsx` — table de données `COLONNES`, `changerTri` qui
 * remet la page à 1 et adopte le sens initial de la nouvelle colonne, `aria-sort` sur les
 * en-têtes.
 *
 * ⚠️ **La classe est `-grille`, jamais `-table`.** `App.css` porte une règle générique
 * `table[class*="-table"]` qui travaille par **sous-chaîne** et couvre les vingt-deux classes de
 * tableau du projet : elle imposerait `table-layout: fixed`, un centrage et des largeurs de
 * 130 px, avec une spécificité qu'aucune règle locale ne bat. C'est le défaut vécu par
 * `radar-tableau`.
 */

/**
 * Les colonnes, et la clé de tri que chacune demande au serveur.
 *
 * ⚠️ **« Délai » n'est pas triable** (`cle: null`), et ce n'est pas un oubli : il est **dérivé
 * après** le découpage de la page. Trier dessus classerait les vingt-cinq lignes reçues et non
 * les deux cents du journal — l'écran paraîtrait cassé. Même raison qui fait trier les notes
 * côté serveur.
 */
const COLONNES = [
  { libelle: 'Vu le', cle: 'observeLe', sensInitial: 'desc', alignement: 'left' },
  { libelle: 'Leviers', cle: null, alignement: 'left' },
  { libelle: 'Phrase affichée', cle: null, alignement: 'left' },
  { libelle: 'Délai', cle: null, alignement: 'left' },
  { libelle: 'Observateur', cle: null, alignement: 'left' },
  { libelle: 'Source', cle: 'source', sensInitial: 'asc', alignement: 'left' },
  { libelle: '', cle: null, alignement: 'right' }
];

const JournalObservations = ({
  observations, total, page, totalPages, tri, onTri, onPage,
  saisieAutorisee, onCorriger, onSupprimer, lienExport, levier, libelleSource, libelleGeste
}) => {
  const { survoler, quitter, bulle } = useBulleAuSurvol();
  const [aSupprimer, setASupprimer] = useState(null);

  /**
   * Clic sur un en-tête. Même colonne : on inverse. Colonne différente : on adopte son sens
   * initial — passer de « Vu le décroissant » à « Source » aurait sinon ouvert les sources de
   * Z à A. Et le retour à la page 1 est indispensable : rester page 3 après un changement de
   * tri afficherait les lignes 51 à 75 d'un classement qu'on n'a jamais vu commencer.
   */
  const changerTri = useCallback((colonne) => {
    if (!colonne.cle) return;
    if (tri.sortBy === colonne.cle) {
      onTri({ sortBy: colonne.cle, sortOrder: tri.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      onTri({ sortBy: colonne.cle, sortOrder: colonne.sensInitial });
    }
  }, [tri, onTri]);

  const th = (c) => {
    const actif = c.cle && tri.sortBy === c.cle;
    return (
      <th
        key={c.libelle}
        onClick={() => changerTri(c)}
        aria-sort={actif ? (tri.sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        style={{
          padding: '10px 12px',
          textAlign: c.alignement,
          fontSize: '11px',
          fontWeight: '600',
          color: '#6b7280',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
          cursor: c.cle ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #e5e7eb'
        }}
      >
        {c.libelle}
        {c.cle && <ArrowUpDown size={11} style={{ marginLeft: '4px', opacity: actif ? 1 : 0.3, verticalAlign: 'middle' }} />}
      </th>
    );
  };

  return (
    <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '12px', padding: '14px 16px', borderBottom: '1px solid #f3f4f6'
      }}>
        <div>
          <h3 style={{ fontSize: '15px', fontWeight: '600' }}>Journal des relevés</h3>
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            {total} relevé{total > 1 ? 's' : ''}
            {levier ? ', filtré sur un levier' : ''}
          </p>
        </div>
        {total > 0 && (
          <a
            href={lienExport}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '7px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
              fontSize: '12px', color: '#374151', textDecoration: 'none'
            }}
          >
            <Download size={13} /> Exporter en CSV
          </a>
        )}
      </div>

      {observations.length === 0 ? (
        <p style={{ padding: '28px 16px', textAlign: 'center', fontSize: '13px', color: '#9ca3af' }}>
          Aucun relevé {levier ? 'pour ce levier' : 'encore'}.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* ⚠️ `-grille`, pas `-table` : voir l'en-tête du fichier. */}
          <table className="ksi-journal-grille" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{COLONNES.map(th)}</tr></thead>
            <tbody>
              {observations.map(o => {
                const delai = delaiEnMots(o.delai, libelleGeste);
                return (
                  <tr key={o.id} style={{ borderBottom: '1px solid #f9fafb' }}>
                    <td style={{ padding: '10px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {new Date(o.observeLe).toLocaleString('fr-FR', {
                        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
                      })}
                    </td>

                    <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(o.leviers || []).map(l => (
                          <span key={l} style={{
                            padding: '2px 8px', borderRadius: '999px',
                            backgroundColor: '#f3f4f6', fontSize: '11px', whiteSpace: 'nowrap'
                          }}>
                            {levier && l === levier ? <strong>{l}</strong> : l}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* La phrase, tronquée à l'écran et entière au survol : c'est la pièce du
                        dossier, on doit pouvoir la relire sans ouvrir la fiche. */}
                    <td style={{ padding: '10px 12px', fontSize: '12px', maxWidth: '280px' }}>
                      <span
                        className="ksi-survolable"
                        onMouseEnter={survoler(o.raisonBrute)}
                        onMouseLeave={quitter}
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {o.raisonBrute}
                      </span>
                    </td>

                    {/*
                      ⚠️ Un délai indisponible affiche un tiret et DIT pourquoi au survol. Ni
                      « 0 h », ni une case vide : le premier annoncerait une relance immédiate,
                      la seconde se prendrait pour un défaut d'affichage.
                    */}
                    <td style={{ padding: '10px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {delai ? (
                        <span
                          className="ksi-survolable"
                          onMouseEnter={survoler(
                            `Compté depuis « ${libelleGeste(o.delai.geste)} », consigné le `
                            + `${new Date(o.delai.faitLe).toLocaleString('fr-FR')}.`
                            + (o.delai.origine === 'deduit'
                              ? "\n\nGeste déduit : c'est le plus proche antérieur de cet observateur."
                              : "\n\nGeste désigné par l'observateur.")
                          )}
                          onMouseLeave={quitter}
                        >
                          {delai}
                        </span>
                      ) : (
                        <span
                          className="ksi-survolable"
                          onMouseEnter={survoler(o.delai?.message || "Le délai n'est pas mesuré.")}
                          onMouseLeave={quitter}
                          style={{ color: '#d1d5db' }}
                        >
                          —
                        </span>
                      )}
                    </td>

                    <td style={{ padding: '10px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {o.observateur?.name || o.observateur?.email || '—'}
                    </td>

                    <td style={{ padding: '10px 12px', fontSize: '11px', color: '#6b7280' }}>
                      {libelleSource(o.source)}
                    </td>

                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {saisieAutorisee && (
                        <>
                          <button onClick={() => onCorriger(o)} title="Corriger" style={{
                            border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', padding: '4px'
                          }}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => setASupprimer(o.id)} title="Supprimer" style={{
                            border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px'
                          }}>
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
          padding: '12px', borderTop: '1px solid #f3f4f6', fontSize: '12px'
        }}>
          <button onClick={() => onPage(page - 1)} disabled={page <= 1} style={{
            padding: '5px 10px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer',
            opacity: page <= 1 ? 0.5 : 1
          }}>Précédent</button>
          <span style={{ color: '#6b7280' }}>Page {page} sur {totalPages}</span>
          <button onClick={() => onPage(page + 1)} disabled={page >= totalPages} style={{
            padding: '5px 10px', borderRadius: '6px', border: '1px solid #d1d5db',
            backgroundColor: '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer',
            opacity: page >= totalPages ? 0.5 : 1
          }}>Suivant</button>
        </div>
      )}

      {/* Une suppression se confirme en ligne : une boîte de dialogue native bloquerait l'onglet
          et la règle du projet est de ne jamais en déclencher. */}
      {aSupprimer && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          padding: '12px 16px', backgroundColor: '#fef2f2', borderTop: '1px solid #fecaca', fontSize: '12px'
        }}>
          <span style={{ color: '#991b1b' }}>Supprimer ce relevé ? Son verbatim sera perdu.</span>
          <span style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setASupprimer(null)} style={{
              padding: '5px 10px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', cursor: 'pointer'
            }}>Annuler</button>
            <button onClick={() => { onSupprimer(aSupprimer); setASupprimer(null); }} style={{
              padding: '5px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer'
            }}>Supprimer</button>
          </span>
        </div>
      )}

      {bulle}
    </div>
  );
};

export default JournalObservations;
